import { Network } from ':fat/core/networks';

import { db } from './kysely';
import { Address, Broadcast, Utxo, Xpub } from './schema-types';

// These queries should be passed to the Database class via the DatabaseWorker
// action, like so:
//
//     import { query } from ':fat/messaging/entities/databaseWorker/actions';
//
//     // Example query
//     function getAllXpubs() {
//       return db.selectFrom('xpub').selectAll().compile()
//     }
//
//     // Usage:
//     const xpubs = await query(getAllXpubs());

export function ensureXpubsAreSaved(xpubs: readonly Xpub[]) {
  return db
    .insertInto('xpub')
    .values(xpubs)
    .onConflict((oc) => oc.doNothing())
    .returning('id')
    .compile();
}

export function addFeesOwedToXpub(xpubID: number | null, feesOwed: number) {
  if (xpubID === null) {
    throw new Error('xpub is not saved, cannot update');
  }

  return db.updateTable('xpub').where('id', '=', xpubID).set('fees_owed', feesOwed).compile();
}

export function getAllXpubsWithFingerprint(fingerprint: number) {
  return db.selectFrom('xpub').selectAll().where('fingerprint', '=', fingerprint).compile();
}

export function ensureAddressesAreSaved(addresses: readonly Address[]) {
  return db
    .insertInto('address')
    .values(addresses)
    .onConflict((oc) => oc.doNothing())
    .returningAll()
    .compile();
}

export function ensureUtxosAreSaved(utxos: readonly Utxo[]) {
  return db
    .insertInto('utxo')
    .values(utxos)
    .onConflict((oc) => oc.doNothing())
    .compile();
}

export function removeAddresses(addresses: readonly Address[]) {
  const addressIds = addresses.map((a) => a.id).filter((id) => id !== null);
  return db.deleteFrom('address').where('id', 'in', addressIds).compile();
}

export function lookupAddresses(network: Network, addresses: readonly Address[]) {
  const addrs = addresses.map((a) => a.addr);
  return db
    .selectFrom('address')
    .selectAll()
    .where('network', '=', network)
    .where('addr', 'in', addrs)
    .compile();
}

export function getUtxosForAddresses(addresses: readonly Address[]) {
  const addressIds = addresses.map((a) => a.id).filter((id) => id !== null);
  return db.selectFrom('utxo').selectAll().where('address_id', 'in', addressIds).compile();
}

export function getUtxosWithAddressInfoForXpub(xpub: Xpub) {
  return db
    .selectFrom('utxo')
    .innerJoin('address', 'address.id', 'utxo.address_id')
    .innerJoin('xpub', 'xpub.id', 'address.xpub_id')
    .where('xpub.network', '=', xpub.network)
    .where('xpub.fingerprint', '=', xpub.fingerprint)
    .select([
      'utxo.id',
      'utxo.tx_hash',
      'utxo.vout_index',
      'utxo.value',
      'utxo.path as full_path',
      'address.addr',
      'address.kind',
      'address.path as relative_path',
      'address.redeem_script',
    ])
    .orderBy('value desc')
    .compile();
}

export function getAddressesWithEthBalances(fingerprint: number) {
  return db
    .selectFrom('address')
    .select(['address.addr', 'address.eth_balance', 'address.path'])
    .innerJoin('xpub', 'xpub.id', 'address.xpub_id')
    .where('xpub.fingerprint', '=', fingerprint)
    .where('eth_balance', 'is not', null)
    .compile();
}

export function balanceForXpub(xpub: Xpub) {
  return db
    .selectFrom('utxo')
    .innerJoin('address', 'address.id', 'utxo.address_id')
    .innerJoin('xpub', 'xpub.id', 'address.xpub_id')
    .where('xpub.id', '=', xpub.id)
    .select(({ fn }) => [fn.sum<number>('utxo.value').as('balance')])
    .compile();
}

export function utxoBalancesByNetwork(fingerprint: number) {
  return db
    .selectFrom('utxo')
    .innerJoin('address', 'address.id', 'utxo.address_id')
    .innerJoin('xpub', 'xpub.id', 'address.xpub_id')
    .where('xpub.fingerprint', '=', fingerprint)
    .select(({ fn }) => ['xpub.network as network', fn.sum<number>('utxo.value').as('balance')])
    .groupBy('xpub.network')
    .compile();
}

export function addressesScannedForXpub(xpub: Xpub) {
  return db
    .selectFrom('address')
    .where('xpub_id', '=', xpub.id)
    .select(({ fn }) => [fn.count<number>('id').as('address_count')])
    .compile();
}

export function deleteOldBroadcasts() {
  return db.deleteFrom('broadcast').compile();
}

export function initBroadcast(broadcast: Broadcast) {
  return db
    .insertInto('broadcast')
    .values([broadcast])
    .onConflict((oc) => oc.doNothing())
    .compile();
}

export function updateBroadcast(id: number, attempts: number, status: string, reason?: string) {
  return db
    .updateTable('broadcast')
    .where('id', '=', id)
    .set({ attempts, status, ...(reason ? { failure_reason: reason } : {}) })
    .compile();
}

export function getBroadcastsForFingerprint(fingerprint: number) {
  return db.selectFrom('broadcast').selectAll().where('fingerprint', '=', fingerprint).compile();
}

export function broadcastableBroadcasts(fingerprint: number, maxAttempts: number) {
  return db
    .selectFrom('broadcast')
    .innerJoin('xpub', (join) =>
      join
        .onRef('xpub.fingerprint', '=', 'broadcast.fingerprint')
        .onRef('xpub.network', '=', 'broadcast.network'),
    )
    .selectAll('broadcast')
    .select('xpub.key as xpubkey')
    .where('broadcast.fingerprint', '=', fingerprint)
    .where('broadcast.attempts', '<', maxAttempts)
    .where('broadcast.status', '!=', 'SUCCESS')
    .compile();
}
